/**
 * Mobile Bottom Navigation (SCR-000)
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-000 Application Shell - Mobile nav section
 */

import { Link } from 'react-router-dom';
import { Icon, type IconName } from '@myvault/shared/components';
import { cn } from '@myvault/shared/utils';

interface MobileNavProps {
  currentPath: string;
}

interface NavItem {
  icon: IconName;
  label: string;
  href?: string;
  action?: string;
  primary?: boolean;
}

const mobileNavItems: NavItem[] = [
  { icon: 'home', label: 'Home', href: '/dashboard' },
  { icon: 'folder', label: 'Documenten', href: '/documents' },
  { icon: 'cloudArrowUp', label: 'Upload', href: '/documents/upload', primary: true },
  { icon: 'sparkles', label: 'Prompts', href: '/prompt-builder/templates' },
  { icon: 'menu', label: 'Meer', action: 'menu' },
];

export function MobileNav({ currentPath }: MobileNavProps) {
  const isActive = (href?: string) => {
    if (!href) return false;
    if (href === '/dashboard') return currentPath === '/dashboard';
    return currentPath.startsWith(href);
  };

  return (
    <nav
      // GAP-003: Hide on tablet (md) since we show navigation rail in sidebar
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white md:hidden"
      aria-label="Mobiele navigatie"
    >
      {/* GAP-006: Increased height to ensure 44px minimum touch targets */}
      <div className="flex h-[68px] items-center justify-around">
        {mobileNavItems.map((item, index) => {
          if (item.action === 'menu') {
            // This is handled by the parent component - just a placeholder trigger
            return (
              <button
                key={index}
                // GAP-006: 44px minimum touch target
                className="flex min-h-[44px] min-w-[44px] flex-col items-center justify-center px-2 py-1 text-gray-500"
                aria-label="Meer opties"
              >
                <Icon name={item.icon} variant="outline" size="md" />
                <span className="mt-0.5 text-[10px]">{item.label}</span>
              </button>
            );
          }

          const active = isActive(item.href);

          if (item.primary) {
            return (
              <Link
                key={index}
                to={item.href!}
                // GAP-006: Primary action already has 48px (h-12 w-12), exceeds 44px minimum
                className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700"
                aria-label={item.label}
              >
                <Icon name={item.icon} variant="solid" size="lg" />
              </Link>
            );
          }

          return (
            <Link
              key={index}
              to={item.href!}
              // GAP-006: 44px minimum touch target
              className={cn(
                'flex min-h-[44px] min-w-[44px] flex-col items-center justify-center px-2 py-1',
                active ? 'text-blue-600' : 'text-gray-500'
              )}
            >
              <Icon name={item.icon} variant={active ? 'solid' : 'outline'} size="md" />
              <span className="mt-0.5 text-[10px]">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
